import axios from 'app/infrastructure/axiosInstance';
import * as config from 'app/configs/configs';
import { Filters, IErrorResponse, IPagination, IValidationErrorResponse, Response, Sorters } from 'app/modules/tp/_apiResponse';
import { AxiosResponse } from 'axios';

export type QueryParams = {
    [key: string]: (string | number | boolean | undefined) | Filters | Sorters
    sorters?: Sorters | string ,
    filters?: Filters | string ,
}

export interface PaginationQueryParams extends QueryParams {
    page: number,
    sorters?: Sorters,
    filters?: Filters
}

const isResponseValid = <T>(response: AxiosResponse<Response<T>>): boolean => (
    response.status >= 200 && response.status < 300 && response?.data?.success
)

export const extractData = <T>(response: AxiosResponse<Response<T>>): Response<T> => {
    if (!isResponseValid(response)) {
        const backendErrorCode = (response?.data?.data?.code) ?? 0
        switch (response.status) {
            case 422:
                if (parseInt(backendErrorCode) === 23000) {
                    return {...response?.data, type: "foreign_relation" } as IErrorResponse
                }
                return { ...response?.data, type: "validation" } as IValidationErrorResponse
            default:
                return response?.data as IErrorResponse
        }
    }
    return response.data as Response<T>
}

// @TODO: add operator to filters type and use it here
export const SerializeFilters = (filters?: Filters | string): string => {
    if(!filters) {return ""}
    if(typeof filters === "string") {return filters};
    return Object.keys(filters).map(key => `${key}:${filters[key]}`).join(";")
}

// @TODO: add direction to sorter type and use it here
export const SerializeSorters = (sorters?: Sorters | string): string => {
    if(!sorters) {return ""};
    if(typeof sorters === "string") {return sorters};
    return Object.keys(sorters).map(key => `${key}:${sorters[key]}`).join(";")
}

export const serializeQueryParams = (queryParams: QueryParams): QueryParams => ({
    ...queryParams,
    filters: SerializeFilters(queryParams?.filters),
    sorters: SerializeSorters(queryParams?.sorters)
})


export type ResourceAction = "create" | "update" | "delete" | "duplicate" | "reload"
export type ResourceGetter = "get" | "getAll" | "getAllNoPagination" | "getAllFull" | "getAllFullNoPagination"

export type Resource<T> = {
    getAll: (queryParams: PaginationQueryParams) => Promise<Response<IPagination<T>>>,
    getAllNoPagination: (queryParams?: QueryParams) => Promise<Response<Array<T>>>,
    getAllNoResourceNoPagination: (queryParams?: QueryParams) => Promise<Response<Array<T>>>,
    getAllFull: (queryParams: PaginationQueryParams) => Promise<Response<IPagination<T>>>,
    getAllFullNoPagination: (queryParams?: QueryParams) => Promise<Response<Array<T>>>,
    get: (id: number, queryParams: QueryParams | undefined) => Promise<Response<T>>,
    getFull: (id: number, queryParams: QueryParams | undefined) => Promise<Response<T>>,
    create: (data: T) => Promise<Response<T>>,
    update: (id: number, data: T) => Promise<Response<T>>,
    delete: (id: number) => Promise<Response<T>>,
    duplicate: (id: number) => Promise<Response<T>>,
}

export function makeResource<T>(endpointName: string) {


    const baseApiUrl = config.getBaseApiUrl();
    const apiUrl = `${baseApiUrl}/${endpointName}`;

    const getAll = async <R>(queryParams: QueryParams) =>
        extractData(await axios.get<Response<R>>(apiUrl,
            { params: { ...queryParams } }
        ))

    const get =
        async <R>(id: number, queryParams: QueryParams | undefined) =>
            extractData<T>(await axios.get<Response<R>>(`${apiUrl}/${id}`,
                { params: { ...queryParams } }
            ));

    return {
        getAll: (queryParams: QueryParams) => getAll<IPagination<T>>(queryParams),
        getAllNoPagination: (queryParams: QueryParams) => getAll<Array<T>>({ ...(queryParams ?? []), get_all: true }),
        getAllNoResourceNoPagination: (queryParams: QueryParams) => getAll<Array<T>>({ ...(queryParams ?? []), get_all_no_resource: true }),
        getAllFull: (queryParams: QueryParams) => getAll<IPagination<T>>({ ...queryParams, get_full: true }),
        getAllFullNoPagination: (queryParams: QueryParams) => getAll<Array<T>>({ ...(queryParams ?? []), get_full: true, get_all: true }),

        get: (id: number, queryParams: QueryParams | undefined) => get<T>(id, queryParams),
        getFull: (id: number, queryParams: QueryParams | undefined) => get<T>(id, { ...queryParams, get_full: true }),

        create:
            async (data: T) =>
                extractData<T>(await axios.post<Response<T>>(apiUrl, data)),

        update:
            async (id: number, data: T) =>
                extractData<T>(await axios.put<Response<T>>(`${apiUrl}/${id}`, data)),

        delete:
            async (id: number) =>
                extractData<T>(await axios.delete<Response<T>>(`${apiUrl}/${id}`)),
        duplicate:
            async (id: number) =>
                extractData<T>(await axios.post<Response<T>>(`${apiUrl}/duplicate/${id}`)),
    } as Resource<T>
}




